import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  UserCircle2, IdCard, Info, Phone, ShieldAlert, LogOut, Trash2,
  ChevronRight, FileText, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, setToken } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import DigitalCard from "@/components/DigitalCard";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_COLORS = {
  received: "bg-brand-tint text-brand",
  in_review: "bg-[#FFF3E0] text-[#E65100]",
  resolved: "bg-[#E8F5E9] text-[#2E7D32]",
};

export default function Profile() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api("/membership/me").then((m) => setMember(m.member)).catch(() => {});
    api("/complaints/mine").then(setComplaints).catch(() => {});
  }, []);

  const isActive = member?.status === "active";

  const statusLabel = (s) => (s === "resolved" ? t("stResolved") : s === "in_review" ? t("stInReview") : t("stReceived"));

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api("/account", { method: "DELETE" });
      setToken(null);
      toast.success(t("profDelete"));
      window.location.href = "/login";
    } catch (e) {
      toast.error(e.message || "Failed");
      setDeleting(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full bg-brand-tint">
          {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : <UserCircle2 size={40} className="text-brand" />}
        </div>
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-black text-ink">{user?.name}</h1>
          <p className="truncate text-sm text-ink-muted">{user?.email}</p>
        </div>
      </div>

      {/* Card section */}
      <div className="mt-8">
        <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-ink-muted">{t("profCardSection")}</h2>
        {isActive ? (
          <button onClick={() => navigate("/card")} data-testid="profile-card" className="block w-full text-left">
            <DigitalCard member={member} />
          </button>
        ) : (
          <button onClick={() => navigate("/membership")} data-testid="profile-become-member" className="flex w-full items-center gap-4 rounded-2xl border border-brand/30 bg-brand-tint p-5 text-left">
            <IdCard className="text-brand" size={28} />
            <div className="flex-1">
              <div className="font-bold text-brand-dark">{t("profBecome")}</div>
              <div className="text-sm text-ink-soft">{t("profBecomeSub")}</div>
            </div>
            <ChevronRight className="text-brand" />
          </button>
        )}
      </div>

      {/* Complaints */}
      <div className="mt-8">
        <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-ink-muted">{t("profComplaints")}</h2>
        <div className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white">
          {complaints.length === 0 && (
            <div className="p-5 text-center text-sm text-ink-muted">{t("grvOkSub") || "—"}</div>
          )}
          {complaints.map((c) => (
            <Link key={c.ticket_id} to={`/complaint/${c.ticket_id}`} data-testid={`complaint-${c.ticket_id}`} className="flex items-center gap-3 p-4 hover:bg-brand-tint/40">
              <FileText size={18} className="text-brand" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-ink">{c.subject}</div>
                <div className="text-xs text-ink-muted">{c.ticket_id}</div>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${STATUS_COLORS[c.status] || STATUS_COLORS.received}`}>
                {statusLabel(c.status)}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* More */}
      <div className="mt-8">
        <h2 className="mb-3 text-xs font-bold uppercase tracking-widest text-ink-muted">{t("profMore")}</h2>
        <div className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white">
          <MenuRow icon={Info} label={t("profAboutItem")} onClick={() => navigate("/about")} testid="menu-about" />
          <MenuRow icon={Phone} label={t("profContactItem")} onClick={() => navigate("/contact")} testid="menu-contact" />
          <div className="flex items-center justify-between p-4">
            <span className="flex items-center gap-3 text-sm font-semibold text-ink"><UserCircle2 size={18} className="text-brand" /> {t("profLanguageItem")}</span>
            <LanguageSwitcher />
          </div>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/privacy`} target="_blank" rel="noreferrer" data-testid="menu-privacy" className="flex items-center justify-between p-4 hover:bg-brand-tint/40">
            <span className="flex items-center gap-3 text-sm font-semibold text-ink"><ShieldAlert size={18} className="text-brand" /> {t("profPrivacy")}</span>
            <ChevronRight size={18} className="text-ink-muted" />
          </a>
        </div>
      </div>

      {/* Danger zone */}
      <div className="mt-8 space-y-3">
        <Button data-testid="logout-button" onClick={logout} variant="outline" className="h-11 w-full rounded-full font-bold">
          <LogOut size={17} className="mr-2" /> {t("profLogout")}
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="menu-delete-account" className="flex w-full items-center justify-center gap-2 rounded-full border border-[#F2B8B5] bg-[#FDECEA] py-2.5 text-sm font-bold text-[#C62828]">
              <Trash2 size={16} /> {t("profDelete")}
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("profDelete")}</AlertDialogTitle>
              <AlertDialogDescription>{t("profDeleteWarn")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-account">{t("profDeleteCancel")}</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-delete-account" onClick={deleteAccount} disabled={deleting} className="bg-[#C62828] hover:bg-[#a81f1f]">
                {deleting ? <Loader2 className="animate-spin" /> : t("profDeleteBtn")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function MenuRow({ icon: Icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex w-full items-center justify-between p-4 text-left hover:bg-brand-tint/40">
      <span className="flex items-center gap-3 text-sm font-semibold text-ink"><Icon size={18} className="text-brand" /> {label}</span>
      <ChevronRight size={18} className="text-ink-muted" />
    </button>
  );
}
